var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { openAlert } from './alert/Alert';
import { openToast } from './alert/Toast';
import { Logger } from './utils/Logger';
import { Serializer } from './Serializer';
import { UnknownBeaconError, BeaconError, NetworkType } from '.';
const logger = new Logger('BeaconEvents');
const serializer = new Serializer();
/**
 * The different events that can be emitted by the beacon-sdk
 */
export var BeaconEvent;
(function (BeaconEvent) {
    BeaconEvent["PERMISSION_REQUEST_SENT"] = "PERMISSION_REQUEST_SENT";
    BeaconEvent["PERMISSION_REQUEST_SUCCESS"] = "PERMISSION_REQUEST_SUCCESS";
    BeaconEvent["PERMISSION_REQUEST_ERROR"] = "PERMISSION_REQUEST_ERROR";
    BeaconEvent["OPERATION_REQUEST_SENT"] = "OPERATION_REQUEST_SENT";
    BeaconEvent["OPERATION_REQUEST_SUCCESS"] = "OPERATION_REQUEST_SUCCESS";
    BeaconEvent["OPERATION_REQUEST_ERROR"] = "OPERATION_REQUEST_ERROR";
    BeaconEvent["SIGN_REQUEST_SENT"] = "SIGN_REQUEST_SENT";
    BeaconEvent["SIGN_REQUEST_SUCCESS"] = "SIGN_REQUEST_SUCCESS";
    BeaconEvent["SIGN_REQUEST_ERROR"] = "SIGN_REQUEST_ERROR";
    BeaconEvent["BROADCAST_REQUEST_SENT"] = "BROADCAST_REQUEST_SENT";
    BeaconEvent["BROADCAST_REQUEST_SUCCESS"] = "BROADCAST_REQUEST_SUCCESS";
    BeaconEvent["BROADCAST_REQUEST_ERROR"] = "BROADCAST_REQUEST_ERROR";
    BeaconEvent["LOCAL_RATE_LIMIT_REACHED"] = "LOCAL_RATE_LIMIT_REACHED";
    BeaconEvent["NO_PERMISSIONS"] = "NO_PERMISSIONS";
    BeaconEvent["ACTIVE_ACCOUNT_SET"] = "ACTIVE_ACCOUNT_SET";
    BeaconEvent["ACTIVE_TRANSPORT_SET"] = "ACTIVE_TRANSPORT_SET";
    BeaconEvent["PAIR_INIT"] = "PAIR_INIT";
    BeaconEvent["PAIR_SUCCESS"] = "PAIR_SUCCESS";
    BeaconEvent["CHANNEL_CLOSED"] = "CHANNEL_CLOSED";
    BeaconEvent["P2P_CHANNEL_CONNECT_SUCCESS"] = "P2P_CHANNEL_CONNECT_SUCCESS";
    BeaconEvent["P2P_LISTEN_FOR_CHANNEL_OPEN"] = "P2P_LISTEN_FOR_CHANNEL_OPEN";
    BeaconEvent["INTERNAL_ERROR"] = "INTERNAL_ERROR";
    BeaconEvent["UNKNOWN"] = "UNKNOWN";
})(BeaconEvent || (BeaconEvent = {}));
/**
 * Show a "Request sent" toast
 */
const showSentToast = () => __awaiter(void 0, void 0, void 0, function* () {
    openToast({ body: 'Request sent', timer: 3000 }).catch((toastError) => console.error(toastError));
});
/**
 * Show a "No Permission" alert
 */
const showNoPermissionAlert = () => __awaiter(void 0, void 0, void 0, function* () {
    yield openAlert({
        title: 'No Permission',
        body: 'Please allow the wallet to handle this type of request.'
    });
});
/**
 * Show an error alert
 *
 * @param beaconError The beacon error
 */
const showErrorAlert = (beaconError, buttons) => __awaiter(void 0, void 0, void 0, function* () {
    const error = beaconError.errorType
        ? BeaconError.getError(beaconError.errorType)
        : new UnknownBeaconError();
    yield openAlert({
        title: error.title,
        body: error.description,
        buttons
    });
});
/**
 * Show a rate limit reached toast
 */
const showRateLimitReached = () => __awaiter(void 0, void 0, void 0, function* () {
    openToast({
        body: 'Rate limit reached. Please slow down',
        timer: 3000
    }).catch((toastError) => console.error(toastError));
});
/**
 * Show a "connection successful" alert for 1.5 seconds
 */
const showBeaconConnectedAlert = () => __awaiter(void 0, void 0, void 0, function* () {
    yield openAlert({
        title: 'Success',
        body: 'A wallet has been paired over the beacon network.',
        buttons: [{ text: 'Done', style: 'outline' }],
        timer: 1500
    });
});
/**
 * Show a "connection successful" alert for 1.5 seconds
 */
const showExtensionConnectedAlert = () => __awaiter(void 0, void 0, void 0, function* () {
    yield openAlert({
        title: 'Success',
        body: 'A wallet has been paired.',
        buttons: [{ text: 'Done', style: 'outline' }],
        timer: 1500
    });
});
/**
 * Show a "channel closed" alert for 1.5 seconds
 */
const showChannelClosedAlert = () => __awaiter(void 0, void 0, void 0, function* () {
    yield openAlert({
        title: 'Channel closed',
        body: `Your peer has closed the connection.`,
        buttons: [{ text: 'Done', style: 'outline' }],
        timer: 1500
    });
});
const showInternalErrorAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const alertConfig = {
        title: 'Internal Error',
        body: `${data}`,
        buttons: [{ text: 'Done', style: 'outline' }]
    };
    yield openAlert(alertConfig);
});
/**
 * Show a connect alert with QR code
 *
 * @param data The data that is emitted by the P2P_LISTEN_FOR_CHANNEL_OPEN event
 */
const showQrAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const dataString = JSON.stringify(data);
    console.log(dataString); // TODO: Remove after "copy to clipboard" has been added.
    const base58encoded = yield serializer.serialize(data);
    console.log(base58encoded); // TODO: Remove after "copy to clipboard" has been added.
    const alertConfig = {
        title: 'Choose your preferred wallet',
        body: `<p></p>`,
        pairingPayload: { p2pSyncCode: base58encoded, postmessageSyncCode: base58encoded, preferredNetwork: NetworkType.MAINNET }
    };
    yield openAlert(alertConfig);
});
/**
 * Show a connect alert with QR code
 *
 * @param data The data that is emitted by the PAIR_INIT event
 */
const showPairAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const p2pBase58encoded = yield serializer.serialize(data.p2pPeerInfo);
    const postmessageBase58encoded = yield serializer.serialize(data.postmessagePeerInfo);
    const alertConfig = {
        title: 'Choose your preferred wallet',
        body: `<p></p>`,
        pairingPayload: {
            p2pSyncCode: p2pBase58encoded,
            postmessageSyncCode: postmessageBase58encoded,
            preferredNetwork: data.preferredNetwork
        }
    };
    yield openAlert(alertConfig);
});
/**
 * Show a "Permission Granted" alert
 *
 * @param data The data that is emitted by the PERMISSION_REQUEST_SUCCESS event
 */
const showPermissionSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const { account, output, blockExplorer } = data;
    const alertConfig = {
        title: 'Permission Granted',
        body: `We received permissions for the address <strong>${output.address}</strong>
    <br>
    <br>
    Network: <strong>${output.network.type}</strong>
    <br>
    Permissions: <strong>${output.scopes}</strong>`,
        buttons: [
            {
                text: 'Open Blockexplorer',
                actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
                    const link = yield blockExplorer.getAddressLink(output.address, account.network);
                    window.open(link, '_blank');
                })
            },
            { text: 'Done', style: 'solid' }
        ]
    };
    yield openAlert(alertConfig);
});
/**
 * Show an "Operation Broadcasted" alert
 *
 * @param data The data that is emitted by the OPERATION_REQUEST_SUCCESS event
 */
const showOperationSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const { account, output, blockExplorer } = data;
    const alertConfig = {
        title: 'Operation Broadcasted',
        body: `The transaction has successfully been broadcasted to the network with the following hash. <strong>${output.transactionHash}</strong>`,
        buttons: [
            {
                text: 'Open Blockexplorer',
                actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
                    const link = yield blockExplorer.getTransactionLink(output.transactionHash, account.network);
                    window.open(link, '_blank');
                })
            },
            { text: 'Done', style: 'solid' }
        ]
    };
    yield openAlert(alertConfig);
});
/**
 * Show a "Transaction Signed" alert
 *
 * @param data The data that is emitted by the SIGN_REQUEST_SUCCESS event
 */
const showSignSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const output = data.output;
    const alertConfig = {
        title: 'Payload signed',
        body: `The payload has successfully been signed.
    <br>
    Signature: <strong>${output.signature}</strong>`,
        buttons: [{ text: 'Done', style: 'solid' }]
    };
    yield openAlert(alertConfig);
});
/**
 * Show a "Broadcasted" alert
 *
 * @param data The data that is emitted by the BROADCAST_REQUEST_SUCCESS event
 */
const showBroadcastSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const { network, output, blockExplorer } = data;
    const alertConfig = {
        title: 'Broadcasted',
        body: `The transaction has successfully been broadcasted to the network with the following hash. <strong>${output.transactionHash}</strong>`,
        buttons: [
            {
                text: 'Open Blockexplorer',
                actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
                    const link = yield blockExplorer.getTransactionLink(output.transactionHash, network);
                    window.open(link, '_blank');
                })
            },
            { text: 'Done', style: 'solid' }
        ]
    };
    yield openAlert(alertConfig);
});
const emptyHandler = (eventType) => (data) => __awaiter(void 0, void 0, void 0, function* () {
    logger.log('emptyHandler', eventType, data);
});
/**
 * The default event handlers
 */
export const defaultEventCallbacks = {
    [BeaconEvent.PERMISSION_REQUEST_SENT]: showSentToast,
    [BeaconEvent.PERMISSION_REQUEST_SUCCESS]: showPermissionSuccessAlert,
    [BeaconEvent.PERMISSION_REQUEST_ERROR]: showErrorAlert,
    [BeaconEvent.OPERATION_REQUEST_SENT]: showSentToast,
    [BeaconEvent.OPERATION_REQUEST_SUCCESS]: showOperationSuccessAlert,
    [BeaconEvent.OPERATION_REQUEST_ERROR]: showErrorAlert,
    [BeaconEvent.SIGN_REQUEST_SENT]: showSentToast,
    [BeaconEvent.SIGN_REQUEST_SUCCESS]: showSignSuccessAlert,
    [BeaconEvent.SIGN_REQUEST_ERROR]: showErrorAlert,
    [BeaconEvent.BROADCAST_REQUEST_SENT]: showSentToast,
    [BeaconEvent.BROADCAST_REQUEST_SUCCESS]: showBroadcastSuccessAlert,
    [BeaconEvent.BROADCAST_REQUEST_ERROR]: showErrorAlert,
    [BeaconEvent.LOCAL_RATE_LIMIT_REACHED]: showRateLimitReached,
    [BeaconEvent.NO_PERMISSIONS]: showNoPermissionAlert,
    [BeaconEvent.ACTIVE_ACCOUNT_SET]: emptyHandler(BeaconEvent.ACTIVE_ACCOUNT_SET),
    [BeaconEvent.ACTIVE_TRANSPORT_SET]: emptyHandler(BeaconEvent.ACTIVE_TRANSPORT_SET),
    [BeaconEvent.PAIR_INIT]: showPairAlert,
    [BeaconEvent.PAIR_SUCCESS]: showExtensionConnectedAlert,
    [BeaconEvent.P2P_CHANNEL_CONNECT_SUCCESS]: showBeaconConnectedAlert,
    [BeaconEvent.P2P_LISTEN_FOR_CHANNEL_OPEN]: showQrAlert,
    [BeaconEvent.CHANNEL_CLOSED]: showChannelClosedAlert,
    [BeaconEvent.INTERNAL_ERROR]: showInternalErrorAlert,
    [BeaconEvent.UNKNOWN]: emptyHandler(BeaconEvent.UNKNOWN)
};
/**
 * Handles beacon events
 */
export class BeaconEventHandler {
    constructor(eventsToOverride = {}) {
        this.callbackMap = {
            [BeaconEvent.PERMISSION_REQUEST_SENT]: [defaultEventCallbacks.PERMISSION_REQUEST_SENT],
            [BeaconEvent.PERMISSION_REQUEST_SUCCESS]: [defaultEventCallbacks.PERMISSION_REQUEST_SUCCESS],
            [BeaconEvent.PERMISSION_REQUEST_ERROR]: [defaultEventCallbacks.PERMISSION_REQUEST_ERROR],
            [BeaconEvent.OPERATION_REQUEST_SENT]: [defaultEventCallbacks.OPERATION_REQUEST_SENT],
            [BeaconEvent.OPERATION_REQUEST_SUCCESS]: [defaultEventCallbacks.OPERATION_REQUEST_SUCCESS],
            [BeaconEvent.OPERATION_REQUEST_ERROR]: [defaultEventCallbacks.OPERATION_REQUEST_ERROR],
            [BeaconEvent.SIGN_REQUEST_SENT]: [defaultEventCallbacks.SIGN_REQUEST_SENT],
            [BeaconEvent.SIGN_REQUEST_SUCCESS]: [defaultEventCallbacks.SIGN_REQUEST_SUCCESS],
            [BeaconEvent.SIGN_REQUEST_ERROR]: [defaultEventCallbacks.SIGN_REQUEST_ERROR],
            [BeaconEvent.BROADCAST_REQUEST_SENT]: [defaultEventCallbacks.BROADCAST_REQUEST_SENT],
            [BeaconEvent.BROADCAST_REQUEST_SUCCESS]: [defaultEventCallbacks.BROADCAST_REQUEST_SUCCESS],
            [BeaconEvent.BROADCAST_REQUEST_ERROR]: [defaultEventCallbacks.BROADCAST_REQUEST_ERROR],
            [BeaconEvent.LOCAL_RATE_LIMIT_REACHED]: [defaultEventCallbacks.LOCAL_RATE_LIMIT_REACHED],
            [BeaconEvent.NO_PERMISSIONS]: [defaultEventCallbacks.NO_PERMISSIONS],
            [BeaconEvent.ACTIVE_ACCOUNT_SET]: [defaultEventCallbacks.ACTIVE_ACCOUNT_SET],
            [BeaconEvent.ACTIVE_TRANSPORT_SET]: [defaultEventCallbacks.ACTIVE_TRANSPORT_SET],
            [BeaconEvent.PAIR_INIT]: [defaultEventCallbacks.PAIR_INIT],
            [BeaconEvent.PAIR_SUCCESS]: [defaultEventCallbacks.PAIR_SUCCESS],
            [BeaconEvent.P2P_CHANNEL_CONNECT_SUCCESS]: [defaultEventCallbacks.P2P_CHANNEL_CONNECT_SUCCESS],
            [BeaconEvent.P2P_LISTEN_FOR_CHANNEL_OPEN]: [defaultEventCallbacks.P2P_LISTEN_FOR_CHANNEL_OPEN],
            [BeaconEvent.CHANNEL_CLOSED]: [defaultEventCallbacks.CHANNEL_CLOSED],
            [BeaconEvent.INTERNAL_ERROR]: [defaultEventCallbacks.INTERNAL_ERROR],
            [BeaconEvent.UNKNOWN]: [defaultEventCallbacks.UNKNOWN]
        };
        this.overrideDefaults(eventsToOverride).catch((overrideError) => {
            logger.error('constructor', overrideError);
        });
    }
    /**
     * A method to subscribe to a specific beacon event and register a callback
     *
     * @param event The event being emitted
     * @param eventCallback The callback that will be invoked
     */
    on(event, eventCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            const listeners = this.callbackMap[event] || [];
            listeners.push(eventCallback);
            this.callbackMap[event] = listeners;
        });
    }
    /**
     * Emit a beacon event
     *
     * @param event The event being emitted
     * @param data The data to be emit
     */
    emit(event, data, eventCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            const listeners = this.callbackMap[event];
            if (listeners && listeners.length > 0) {
                listeners.forEach((listener) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        yield listener(data, eventCallback);
                    }
                    catch (listenerError) {
                        logger.error(`error handling event ${event}`, listenerError);
                    }
                }));
            }
        });
    }
    /**
     * Override beacon event default callbacks. This can be used to disable default alert/toast behaviour
     *
     * @param eventsToOverride An object with the events to override
     */
    overrideDefaults(eventsToOverride) {
        return __awaiter(this, void 0, void 0, function* () {
            Object.keys(eventsToOverride).forEach((untypedEvent) => {
                const eventType = untypedEvent;
                const event = eventsToOverride[eventType];
                if (event) {
                    this.callbackMap[eventType] = [event.handler];
                }
            });
        });
    }
}
//# sourceMappingURL=events.js.map